import React, { useCallback, useState, useRef, useEffect } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import {
  MAKINE_MODELI_OPTIONS,
  MAKINE_URUN_MAP,
  createNewRow,
  calcPersonelBasinaTuketim,
  calcPersonelBasinaMaliyet,
  calcToplamFiyat,
  calcTotals,
  formatTL,
  formatNum,
} from "../../Components/simpleOffer/defaults";

const CELL =
  "border border-gray-300 px-1 py-1 text-center text-[11px] whitespace-nowrap";
const INPUT =
  "w-full text-center bg-transparent outline-none focus:bg-blue-50/60 rounded text-[11px]";
const TH =
  "border border-white/40 bg-[#2d5016] text-white px-0.5 py-1.5 text-[10px] font-semibold text-center leading-tight";

function NumCell({ value, onChange, suffix }) {
  const display = value === 0 ? "0" : String(value);
  return (
    <td className={CELL}>
      <div className="flex items-center justify-center gap-0.5">
        <input
          type="text"
          inputMode="decimal"
          value={display}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9.,]/g, "");
            onChange(parseFloat(raw.replace(",", ".")) || 0);
          }}
          className={INPUT}
        />
        {suffix && (
          <span className="text-gray-400 text-[9px] shrink-0">{suffix}</span>
        )}
      </div>
    </td>
  );
}

// ── Makine modeli secim popup ──
function AddRowPopup({ anchorRef, onSelect, onClose }) {
  const ref = useRef(null);
  const [model, setModel] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target)
      )
        onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose, anchorRef]);

  const urunler = model ? MAKINE_URUN_MAP[model] || [] : [];

  return (
    <div
      ref={ref}
      className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-30 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[200px] py-1"
    >
      {!model ? (
        <>
          <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
            Makine Modeli
          </div>
          {MAKINE_MODELI_OPTIONS.map((m) => (
            <button
              key={m}
              onClick={() => {
                const u = MAKINE_URUN_MAP[m] || [];
                if (u.length <= 1) {
                  onSelect(m, u[0] || "");
                } else {
                  setModel(m);
                }
              }}
              className="w-full text-left px-3 py-1.5 text-[12px] text-gray-700 hover:bg-[#2d5016]/10 hover:text-[#2d5016] transition-colors flex items-center gap-2"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#2d5016]/40 shrink-0" />
              {m}
            </button>
          ))}
        </>
      ) : (
        <>
          <button
            onClick={() => setModel(null)}
            className="w-full text-left px-3 py-1 text-[10px] text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            &larr; {model}
          </button>
          <div className="border-t border-gray-100 my-0.5" />
          <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
            Ürün Seç
          </div>
          {urunler.map((u) => (
            <button
              key={u}
              onClick={() => onSelect(model, u)}
              className="w-full text-left px-3 py-1.5 text-[12px] text-gray-700 hover:bg-[#2d5016]/10 hover:text-[#2d5016] transition-colors flex items-center gap-2"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
              {u}
            </button>
          ))}
        </>
      )}
    </div>
  );
}

// ── Satirlar arasi "+" butonu ──
function RowDivider({ rowIndex, onAdd }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  return (
    <tr>
      <td colSpan={10} className="p-0 border-0 h-0 relative">
        <div className="absolute right-0 top-0 z-10 -translate-y-1/2 translate-x-1/2">
          <div className="relative" ref={btnRef}>
            <button
              onClick={() => setOpen((p) => !p)}
              className={`w-5 h-5 rounded-full flex items-center justify-center border-2 transition-all duration-150 ${
                open
                  ? "bg-[#2d5016] border-[#2d5016] text-white scale-110"
                  : "bg-white border-gray-300 text-gray-400 hover:border-[#2d5016] hover:text-[#2d5016] hover:scale-110"
              }`}
              title="Satır ekle"
            >
              <FaPlus className="w-2 h-2" />
            </button>
            {open && (
              <AddRowPopup
                anchorRef={btnRef}
                onSelect={(m, u) => {
                  onAdd(rowIndex, m, u);
                  setOpen(false);
                }}
                onClose={() => setOpen(false)}
              />
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function OfferTable({ rows, onChange }) {
  const updateRow = useCallback(
    (i, field, value) => {
      onChange((prev) => {
        const next = [...prev];
        next[i] = { ...next[i], [field]: value };
        return next;
      });
    },
    [onChange],
  );

  const addAfter = useCallback(
    (afterIdx, model, urun) => {
      onChange((prev) => {
        const next = [...prev];
        next.splice(afterIdx + 1, 0, createNewRow(model, urun));
        return next;
      });
    },
    [onChange],
  );

  const removeRow = useCallback(
    (i) => onChange((prev) => prev.filter((_, idx) => idx !== i)),
    [onChange],
  );

  const totals = calcTotals(rows);

  return (
    <div className="my-3">
      <table
        className="w-full border-collapse"
        style={{ tableLayout: "fixed" }}
      >
        <colgroup>
          <col style={{ width: "12%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "18%" }} />
        </colgroup>
        <thead>
          <tr>
            <th
              colSpan={10}
              className="border border-white/40 bg-[#2d5016] text-white py-1.5 text-[11px] font-bold"
            >
              Aylık Sabit Tüketim Anlaşması Teklifimiz
            </th>
          </tr>
          <tr>
            <th className={TH}>
              Makine
              <br />
              Modeli
            </th>
            <th className={TH}>Ürün</th>
            <th className={TH}>
              Ürün
              <br />
              Aylık
              <br />
              Kg
            </th>
            <th className={TH}>
              Bardak
              <br />
              Aylık
              <br />
              Adet
            </th>
            <th className={TH}>
              Adet
              <br />
              Fiyat
            </th>
            <th className={TH}>
              Personel
              <br />
              Sayısı
            </th>
            <th className={TH}>
              Personel
              <br />
              Başına
              <br />
              Tüketim
            </th>
            <th className={TH}>
              Personel
              <br />
              Başına
              <br />
              Maliyet
            </th>
            <th className={TH}>
              Makine
              <br />
              Sayısı
            </th>
            <th className={TH}>
              Toplam
              <br />
              Fiyat
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <React.Fragment key={row.id}>
              <tr className="hover:bg-gray-50/50">
                {/* Makine Modeli — serbest metin */}
                <td className={CELL}>
                  <input
                    type="text"
                    value={row.makineModeli}
                    onChange={(e) =>
                      updateRow(i, "makineModeli", e.target.value)
                    }
                    className={`${INPUT} truncate`}
                  />
                </td>
                {/* Urun — serbest metin */}
                <td className={CELL}>
                  <input
                    type="text"
                    value={row.urun}
                    onChange={(e) => updateRow(i, "urun", e.target.value)}
                    className={`${INPUT} font-medium`}
                  />
                </td>
                <NumCell
                  value={row.urunAylikKg}
                  onChange={(v) => updateRow(i, "urunAylikKg", v)}
                />
                <NumCell
                  value={row.bardakAylikAdet}
                  onChange={(v) => updateRow(i, "bardakAylikAdet", v)}
                />
                <NumCell
                  value={row.adetFiyat}
                  onChange={(v) => updateRow(i, "adetFiyat", v)}
                  suffix="TL"
                />
                <NumCell
                  value={row.personelSayisi}
                  onChange={(v) => updateRow(i, "personelSayisi", v)}
                />
                {/* Personel Basina Tuketim (auto: bardakAylikAdet / personelSayisi) */}
                <td className={`${CELL} bg-gray-50`}>
                  {formatNum(calcPersonelBasinaTuketim(row))}
                </td>
                {/* Personel Basina Maliyet (auto: adetFiyat * personelBasinaTuketim) */}
                <td className={`${CELL} bg-gray-50 font-medium`}>
                  {formatTL(calcPersonelBasinaMaliyet(row))}
                </td>
                <NumCell
                  value={row.makineSayisi}
                  onChange={(v) => updateRow(i, "makineSayisi", v)}
                />
                {/* Toplam Fiyat (auto) + Sil butonu */}
                <td className={`${CELL} bg-gray-50 font-semibold`}>
                  <div className="flex items-center justify-center gap-1">
                    <span className="truncate">
                      {formatTL(calcToplamFiyat(row))}
                    </span>
                    {rows.length > 1 && (
                      <button
                        onClick={() => removeRow(i)}
                        className="p-0.5 rounded text-gray-300 hover:text-fifth hover:bg-red-50 transition-colors shrink-0"
                        title="Satırı sil"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {/* Satirlar arasi + */}
              <RowDivider rowIndex={i} onAdd={addAfter} />
            </React.Fragment>
          ))}
          {/* TOPLAM */}
          <tr className="bg-[#2d5016]/10 font-bold">
            <td colSpan={7} className={`${CELL} text-right pr-2 font-bold`}>
              TOPLAM
            </td>
            <td className={`${CELL} font-bold`}>
              {formatTL(totals.toplamPersonelMaliyet)}
            </td>
            <td className={`${CELL} font-bold`}>{totals.toplamMakineSayisi}</td>
            <td className={`${CELL} font-bold text-[#2d5016]`}>
              {formatTL(totals.toplamFiyat)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
